(function (global) {
  'use strict';
  var CACHE_MS = 5 * 60 * 1000;
  var INDEX_TIMEOUT_MS = 8000;
  var PREDICTIVE_TIMEOUT_MS = 4000;
  var MAX_PAGES = 8;
  var MAX_PRODUCTS = 2000;
  var MAX_RESULTS = 100;
  var cache = null;
  var pendingIndex = null;
  var stopwords = new Set('jeg vil ha til med og rundt på i en et skal lage bygge trenger'.split(' ').map(fold));
  // Equivalent terms only. Broad use cases (for example "innkjørsel") are not guessed.
  var synonyms = [['storsekk', 'bigbag'], ['singel', 'hagesingel'], ['grusmatte', 'grusarmering'], ['graa', 'grå']].map(function (group) {
    return group.map(fold);
  });
  var primaryTerms = ['helblokk', 'normalblokk', 'grunnblokk'];
  var accessoryTerms = words('connector geonett hjørne hjørneblokk ende endeblokk halvblokk toppblokk topphelle megatekk såle såleblokk lim');

  function fold(value) {
    return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/å/g, 'a');
  }

  function words(value) {
    return fold(value).replace(/,/g, '.').replace(/(\d)\s*[×x*]\s*(?=\d)/g, '$1x')
      .replace(/(\d)(mm|cm|m2|m3|m)\b/g, '$1 $2').match(/[a-z0-9]+(?:[.-][a-z0-9]+)*/g) || [];
  }

  function queryInfo(query) {
    var normalized = String(query || '').trim().replace(/\s+/g, ' ');
    var expanded = normalized.replace(/\bbig\s+bag\b/gi, 'bigbag').replace(/\becco\s+gravel\b/gi, 'ECCOgravel');
    var tokens = words(expanded).filter(function (word) { return !stopwords.has(word); });
    return { original: normalized, tokens: tokens,
      accessoryIntent: tokens.some(function (token) { return accessoryTerms.some(function (term) { return !!matchToken(token, term); }); }),
      phraseChanged: expanded !== normalized, expanded: expanded };
  }

  function origin() { return global.location.origin; }

  function rootPath() {
    var root = global.Shopify && global.Shopify.routes && global.Shopify.routes.root || '/';
    var url = new URL(root, origin());
    return url.origin === origin() ? url.pathname.replace(/\/?$/, '/') : '/';
  }

  function sameOriginUrl(value) {
    try {
      var url = new URL(value, origin());
      return url.origin === origin() && /^https?:$/.test(url.protocol) ? url : null;
    } catch (_) { return null; }
  }

  function localUrl(value) {
    var url = value && sameOriginUrl(value);
    return url ? url.pathname + url.search + url.hash : '';
  }

  function imageUrl(value) {
    var source = typeof value === 'string' ? value : value && (value.url || value.src);
    if (!source) return undefined;
    try {
      var url = new URL(source, origin());
      if (!/^https?:$/.test(url.protocol)) return undefined;
      return url.href;
    } catch (_) { return undefined; }
  }

  function searchUrl(query) {
    var url = new URL(rootPath() + 'search', origin());
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product,article,page');
    return url.pathname + url.search;
  }

  function abortError() {
    var error = new Error('Search cancelled');
    error.name = 'AbortError';
    return error;
  }

  function waitFor(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abortError());
    return new Promise(function (resolve, reject) {
      function abort() { cleanup(); reject(abortError()); }
      function cleanup() { signal.removeEventListener('abort', abort); }
      signal.addEventListener('abort', abort, { once: true });
      promise.then(function (value) { cleanup(); resolve(value); }, function (error) { cleanup(); reject(error); });
    });
  }

  async function getJSON(url, signal, accept) {
    var allowed = sameOriginUrl(url);
    if (!allowed) throw new Error('Same-origin endpoint required');
    var response = await global.fetch(allowed.href, { signal: signal, credentials: 'same-origin', headers: { Accept: accept || 'application/json' } });
    if (!response.ok) throw new Error('Search response unavailable');
    // A storefront password/HTML response must fail JSON parsing, not become an empty index.
    return response.json();
  }

  function productKey(item) { return item.id != null ? String(item.id).split('/').pop() : (localUrl(item.url) || '').split('?')[0]; }

  function prepareProduct(item) {
    var url = localUrl(item.url);
    if (!url || !item.title) return null;
    var variants = Array.isArray(item.variants) ? item.variants : [];
    return { id: productKey(item), title: String(item.title), url: url, vendor: String(item.vendor || ''),
      type: String(item.type || item.product_type || ''), image: imageUrl(item.image || item.featured_image),
      common: words([item.title, item.type || item.product_type, item.vendor].filter(Boolean).join(' ')),
      variants: variants.map(function (variant) {
        return { title: String(variant.title || ''), sku: String(variant.sku || ''), url: localUrl(variant.url),
          tokens: words([variant.title, variant.sku].filter(Boolean).join(' ')) };
      }), variantsIncomplete: Number(item.variantsCount) > variants.length };
  }

  async function loadIndex(localeRoot) {
    var controller = new AbortController();
    var timer = global.setTimeout(function () { controller.abort(); }, INDEX_TIMEOUT_MS);
    var result = { products: [], pages: 0, total: null, reasons: [], failed: false };
    var seen = new Set();
    try {
      for (var page = 1; page <= MAX_PAGES; page++) {
        var url = new URL(localeRoot + 'collections/all', origin());
        url.searchParams.set('view', 'ms-search-index');
        url.searchParams.set('page', String(page));
        // Shopify serves this JSON-valued Liquid view through its HTML route.
        var body = await getJSON(url.href, controller.signal, 'text/html');
        if (!body || body.version !== 1 || body.page !== page || !Array.isArray(body.products) ||
            !Number.isInteger(body.pages) || body.pages < 0 || body.products.length > 250 ||
            (body.pages === 0 && (body.total !== 0 || body.products.length))) throw new Error('Invalid index page');
        result.pages++;
        result.total = Number.isFinite(body.total) ? body.total : null;
        body.products.forEach(function (item) {
          var product = prepareProduct(item);
          if (product && !seen.has(product.id) && result.products.length < MAX_PRODUCTS) {
            seen.add(product.id); result.products.push(product);
            if (product.variantsIncomplete && result.reasons.indexOf('variant-limit') < 0) result.reasons.push('variant-limit');
          }
        });
        if (page >= body.pages) break;
        if (page === MAX_PAGES) result.reasons.push('product-page-limit');
      }
    } catch (error) {
      result.failed = true;
      result.reasons.push(controller.signal.aborted ? 'index-timeout' : 'index-unavailable');
    } finally { global.clearTimeout(timer); }
    return result;
  }

  function index() {
    var key = rootPath();
    if (cache && cache.key === key && Date.now() - cache.at < CACHE_MS) return Promise.resolve(cache.value);
    if (pendingIndex && pendingIndex.key === key) return pendingIndex.promise;
    var task = { key: key };
    task.promise = loadIndex(key).then(function (value) {
      // A bounded, intentionally capped index can be cached. Failed loads retry next query.
      if (!value.failed) cache = { key: key, at: Date.now(), value: value };
      return value;
    }).finally(function () { if (pendingIndex === task) pendingIndex = null; });
    pendingIndex = task;
    return task.promise;
  }

  async function predictive(query, signal, timeoutMs) {
    var controller = new AbortController();
    var abort = function () { controller.abort(); };
    if (signal) signal.addEventListener('abort', abort, { once: true });
    if (signal && signal.aborted) controller.abort();
    var timer = global.setTimeout(abort, Math.min(PREDICTIVE_TIMEOUT_MS, timeoutMs == null ? PREDICTIVE_TIMEOUT_MS : timeoutMs));
    try {
      var url = new URL(rootPath() + 'search/suggest.json', origin());
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,article,page,query');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');
      var body = await getJSON(url.href, controller.signal);
      if (!body || !body.resources || !body.resources.results) throw new Error('Invalid predictive response');
      if (['products', 'collections', 'articles', 'pages', 'queries'].some(function (key) {
        return body.resources.results[key] != null && !Array.isArray(body.resources.results[key]);
      })) throw new Error('Invalid predictive result groups');
      return { results: body.resources.results, failed: false };
    } catch (_) { return { results: {}, failed: true }; }
    finally {
      global.clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', abort);
    }
  }

  // Optimal string alignment (adjacent Damerau transpositions), bounded short words.
  function distance(a, b) {
    var rows = Array.from({ length: a.length + 1 }, function () { return []; });
    for (var i = 0; i <= a.length; i++) rows[i][0] = i;
    for (var j = 0; j <= b.length; j++) rows[0][j] = j;
    for (i = 1; i <= a.length; i++) for (j = 1; j <= b.length; j++) {
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
    }
    return rows[a.length][b.length];
  }

  function matchToken(query, candidate) {
    if (query === candidate) return { score: 100, replacement: null };
    if (/\d/.test(query)) {
      // Numeric identifiers cannot be typo-corrected. Dimension prefixes end at an x boundary.
      if (/^\d+(?:\.\d+)?$/.test(query) && /^\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)+$/.test(candidate) && candidate.split('x').indexOf(query) >= 0) return { score: 90, replacement: null };
      return /^\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)+$/.test(query) && candidate.indexOf(query + 'x') === 0 ? { score: 80, replacement: null } : null;
    }
    if (candidate.indexOf(query) === 0) return { score: 80, replacement: null };
    for (var s = 0; s < synonyms.length; s++) {
      if (synonyms[s].indexOf(query) >= 0 && synonyms[s].indexOf(candidate) >= 0) return { score: 68, replacement: candidate };
    }
    if (query.length < 4 || query.length > 32 || /\d/.test(candidate)) return null;
    var limit = query.length >= 7 ? 2 : 1;
    var best = limit + 1;
    // Also compare the beginning of a longer product word; "mgaw" can find "megawall".
    for (var length = Math.max(1, query.length - limit); length <= Math.min(candidate.length, query.length + limit); length++) {
      best = Math.min(best, distance(query, candidate.slice(0, length)));
    }
    return best <= limit ? { score: 48 - best * 10, replacement: candidate } : null;
  }

  function matchWords(tokens, candidateWords) {
    var score = 0, replacements = [];
    for (var t = 0; t < tokens.length; t++) {
      var best = null;
      for (var c = 0; c < candidateWords.length; c++) {
        var match = matchToken(tokens[t], candidateWords[c]);
        if (match && (!best || match.score > best.score)) best = match;
        if (best && best.score === 100) break;
      }
      if (!best) return null;
      score += best.score;
      replacements.push(best.replacement || tokens[t]);
    }
    return { score: score, interpreted: replacements.join(' '), changed: replacements.some(function (value, i) { return value !== tokens[i]; }) };
  }

  function rankProduct(product, info) {
    var skuQuery = info.tokens.join('');
    var exact = product.variants.find(function (v) {
      var sku = fold(v.sku).replace(/\s/g, '');
      return sku && (sku === fold(info.expanded).replace(/\s/g, '') || sku === skuQuery);
    });
    if (exact) return { product: product, variant: exact, score: 100000, changed: false, interpreted: '' };
    var best = matchWords(info.tokens, product.common);
    var variant = null;
    // Do not satisfy different query tokens from incompatible colour/size variants.
    if (!best) product.variants.forEach(function (v) {
      var match = matchWords(info.tokens, product.common.concat(v.tokens));
      if (match && (!best || match.score > best.score)) { best = match; variant = v; }
    });
    if (!best) return null;
    var normalizedTitle = words(product.title).filter(function (token) { return !stopwords.has(token); }).join(' '), normalizedQuery = info.tokens.join(' ');
    var bonus = normalizedTitle === normalizedQuery ? 10000 : normalizedTitle.indexOf(normalizedQuery) === 0 ? 1000 : 0;
    // A modest family-search preference, never a replacement for explicit accessory intent.
    if (!info.accessoryIntent && words(product.title).some(function (token) { return primaryTerms.indexOf(token) >= 0; })) bonus += 20;
    return { product: product, variant: variant, score: best.score + bonus, changed: best.changed, interpreted: best.interpreted };
  }

  function cleanResult(item) {
    var url = localUrl(item.url);
    if (!url || !(item.title || item.text)) return null;
    return { id: item.id, title: String(item.title || item.text), url: url, image: imageUrl(item.image || item.featured_image) };
  }

  function finitePrice(value) {
    if (value == null || value === '') return undefined;
    var price = Number(value);
    return Number.isFinite(price) && price >= 0 ? price : undefined;
  }

  function navigationResults(info) {
    var groups = { collections: [], articles: [], pages: [] };
    if (!global.document || !global.document.querySelectorAll) return groups;
    // Use the menu that is actually rendered, including merchant-maintained labels.
    Array.from(global.document.querySelectorAll('.ms-navigation-links a')).forEach(function (anchor) {
      var item = cleanResult({ title: String(anchor.textContent || '').trim(), url: anchor.getAttribute('href') });
      if (!item) return;
      var match = matchWords(info.tokens, words(item.title));
      if (!match) return;
      var type = /\/collections\//.test(item.url) ? 'collections' : /\/pages\//.test(item.url) ? 'pages' : /\/blogs\/[^/]+\/[^/?#]+/.test(item.url) ? 'articles' : null;
      if (type) groups[type].push({ item: item, score: match.score });
    });
    Object.keys(groups).forEach(function (type) {
      groups[type] = groups[type].sort(function (a, b) { return b.score - a.score; }).map(function (entry) { return entry.item; });
    });
    return groups;
  }

  function mergeLinks(local, remote) {
    var seen = new Set();
    return local.concat((remote || []).map(cleanResult).filter(Boolean)).filter(function (item) {
      if (seen.has(item.url)) return false;
      seen.add(item.url); return true;
    }).slice(0, MAX_RESULTS);
  }

  async function search(options) {
    options = options || {};
    var info = queryInfo(options.query), signal = options.signal;
    var empty = { products: [], collections: [], articles: [], pages: [], suggestions: [], interpretedQuery: '', incomplete: false };
    if (signal && signal.aborted) throw abortError();
    if (info.original.length < 2 || !info.tokens.length) return empty;
    var predictiveDeadline = Date.now() + PREDICTIVE_TIMEOUT_MS;
    var sources = await waitFor(Promise.all([index(), predictive(info.expanded, signal)]), signal);
    if (signal && signal.aborted) throw abortError();
    var catalog = sources[0], response = sources[1], prediction = response.results, navigation = navigationResults(info);
    var products = new Map(), current = new Map();
    catalog.products.forEach(function (p) { products.set(p.id, p); });
    (prediction.products || []).forEach(function (item) {
      var product = prepareProduct(item);
      if (!product) return;
      current.set(product.id, item);
      if (!products.has(product.id)) products.set(product.id, product);
    });
    var ranked = Array.from(products.values()).map(function (p) { return rankProduct(p, info); }).filter(Boolean)
      .sort(function (a, b) { return b.score - a.score || a.product.title.localeCompare(b.product.title, 'nb'); }).slice(0, MAX_RESULTS);
    var interpreted = '';
    var alternatives = Array.from(new Set(ranked.filter(function (r) { return r.changed; }).map(function (r) { return r.interpreted; })));
    if (ranked.length && ranked[0].changed && alternatives.length === 1 && !ranked.some(function (r) { return !r.changed; })) interpreted = alternatives[0];
    else if (info.phraseChanged && ranked.length) interpreted = info.expanded;
    var remaining = predictiveDeadline - Date.now();
    if (interpreted && fold(interpreted) !== fold(info.expanded) && ranked[0].changed && remaining > 0) {
      // One unambiguous correction may enrich cards, within the original predictive deadline.
      var corrected = await waitFor(predictive(interpreted, signal, remaining), signal);
      if (!corrected.failed) {
        prediction = corrected.results;
        response.failed = false;
        (prediction.products || []).forEach(function (item) {
          var product = prepareProduct(item);
          if (product) current.set(product.id, item);
        });
      }
    }
    var results = ranked.map(function (rank) {
      var p = rank.product, live = current.get(p.id);
      var item = { id: p.id, title: p.title, url: rank.variant && rank.variant.url || p.url, vendor: p.vendor, image: p.image };
      if (rank.variant) { item.variantTitle = rank.variant.title; item.matchedSku = rank.variant.sku; }
      // Predictive product prices are NOK major units, never Shopify Liquid cents.
      // A product-level price cannot be asserted as an exact matched variant price.
      if (live && !rank.variant) {
        var price = finitePrice(live.price != null ? live.price : live.price_min);
        if (price !== undefined) item.price = price;
        if (typeof live.available === 'boolean') item.available = live.available;
      }
      return item;
    });
    var suggestions = (prediction.queries || []).map(function (item) {
      var text = typeof item === 'string' ? item : item.text || item.title;
      return text && matchWords(info.tokens, words(text)) ? { title: String(text), text: String(text), url: searchUrl(text) } : null;
    }).filter(Boolean);
    if (!interpreted) alternatives.slice(0, 5).forEach(function (text) {
      if (!suggestions.some(function (s) { return s.text === text; })) suggestions.push({ title: text, text: text, url: searchUrl(text) });
    });
    var reasons = catalog.reasons.slice();
    if (response.failed) reasons.push('predictive-unavailable');
    return { products: results,
      collections: mergeLinks(navigation.collections, prediction.collections),
      articles: mergeLinks(navigation.articles, prediction.articles),
      pages: mergeLinks(navigation.pages, prediction.pages), suggestions: suggestions,
      interpretedQuery: interpreted, incomplete: reasons.length > 0,
      coverage: { complete: reasons.length === 0, indexedProducts: catalog.products.length, catalogProductsBeforeVisibilityFilter: catalog.total, pages: catalog.pages, reasons: reasons } };
  }

  global.MSSearchProvider = { search: search };
})(window);
